import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import AlertCircleFilledIcon from 'ext-ui/svg/icon/AlertCircleFilledIcon'
import moment, { Moment } from 'moment-timezone'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import AmountInputField from 'src/components/AmountInputField/AmountInputField'
import Box from 'src/components/Box/Box'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import ErrorList from 'src/components/ErrorList/ErrorList'
import ExpenseFlyoutContent from 'src/components/ExpenseFlyoutContent/ExpenseFlyoutContent'
import MileageRouteSection, {
  MileageRouteData,
} from 'src/components/ExpenseForm/MileageMap/MileageRouteSection'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import NotesInput from 'src/components/NotesInput/NotesInput'
import ReceiptsBox from 'src/components/ReceiptsBox/ReceiptsBox'
import SingleDateInputField from 'src/components/SingleDateInputField/SingleDateInputField'
import SvgExpand from 'src/components/Svgs/SvgExpand'
import TextDropdown from 'src/components/TextDropdown/TextDropdown'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { ExtendCurrency } from 'src/enums'
import {
  FormFieldsValidation,
  useFlyoutDispatch,
  useFormFields,
} from 'src/hooks'
import Crncy from 'src/localization/Crncy'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import {
  ExpenseModel,
  ExpenseReportStatus,
  ExpenseType,
} from 'src/models/expenseReport.model'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import { closeModal, connectedPush } from 'src/navigation/Lnk'
import {
  addExpenseUrl,
  expenseReportUrl,
  expensesOutOfPocketReportsUrl,
  managerReviewReimbursementURL,
  myWalletReimbursementURL,
} from 'src/navigation/urls'
import { LocationState } from 'src/redux/createRootReducer'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { momentIsEmpty } from 'src/utils/validation/moment.validation'
import {
  numberIsEmpty,
  numberMustBeGreaterThan,
} from 'src/utils/validation/number.validation'
import { stringIsEmpty } from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

export type ExpenseFields = {
  expenseReportId?: string
  expenseName?: string
  currencyCode?: string
  amountCents?: number
  expenseDate?: Moment
  notes?: string
  receiptAttachmentIds?: string[]
  type?: ExpenseType
  distance?: number
  mileageRate?: number
  useMileageRoute?: boolean
  mileageRoute?: {
    origin: string
    stops?: string[]
    destination: string
    distance: number
    staticMapApiUrl?: string
  } | null
}

interface ExpenseFormProps {
  expenseReportId: string
  expense?: ExpenseModel
  isSubmitter?: boolean
  isApprover?: boolean
  isManager?: boolean
  handleScroll?: () => void
  loading?: boolean
  negativeButton?: {
    loading: boolean
    text: React.ReactNode | string
    onClick: () => void
  }
  baseFlatFields?: ExpenseFields
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  receiptAttachments: ReceiptAttachmentModel[]
  uploadingAttachmentThumbnails: Record<string, string>
  uploadAttachments: (attachments: PostReceiptAttachmentRequest[]) => void
  deleteAttachment: (attachmentId: string) => void
  buttonText: React.ReactNode
  fieldsEditable: boolean
  notesAndReceiptsEditable: boolean
  expenseReportStatus?: ExpenseReportStatus
  mileageRate?: number
  onSuccessfulSubmit: (
    body: ExpenseFields,
    amountToBeReimbursed: number,
    isExpenseTypeMileage: boolean,
  ) => void
}

const ExpenseForm: React.FC<ExpenseFormProps> = (props): React.ReactNode => {
  const translate = useTranslate()

  const locale = useSelector(selectNullSafeLocaleFromState)
  const { show_mileage_route_map: mileageRouteMapEnabled } =
    useSelector(selectFeatureFlaggerFromState) ?? {}
  const [notesCharacters, setNotesCharacters] = useState<number>(
    props.baseFlatFields?.notes?.length ?? 0,
  )
  const [amountToBeReimbursed, setAmountToBeReimbursed] = useState<number>(
    props.baseFlatFields?.amountCents ?? 0,
  )
  const [isExpenseTypeMileage, setIsExpenseTypeMileage] = useState<boolean>(
    props.expense?.type === ExpenseType.Mileage,
  )
  const [mileageRouteData, setMileageRouteData] = useState<
    MileageRouteData | undefined
  >(
    props.baseFlatFields?.mileageRoute
      ? {
          origin: props.baseFlatFields.mileageRoute.origin,
          stops: props.baseFlatFields.mileageRoute.stops ?? [],
          destination: props.baseFlatFields.mileageRoute.destination,
          distance: props.baseFlatFields.mileageRoute.distance,
          staticMapApiUrl: props.baseFlatFields.mileageRoute.staticMapApiUrl,
        }
      : undefined,
  )
  const [useMileageRoute, setUseMileageRoute] = useState<boolean>(
    props.baseFlatFields?.useMileageRoute ?? true,
  )
  const [mileageRouteError, setMileageRouteError] = useState<
    string | undefined
  >(undefined)

  useEffect(() => {
    const route = props.baseFlatFields?.mileageRoute
    if (route) {
      setMileageRouteData({
        origin: route.origin,
        stops: route.stops ?? [],
        destination: route.destination,
        distance: route.distance,
        staticMapApiUrl: route.staticMapApiUrl,
      })
    } else {
      setMileageRouteData(undefined)
    }
  }, [props.baseFlatFields?.mileageRoute])

  const dispatch = useFlyoutDispatch()
  const history = useHistory<LocationState>()
  const prevUrl = history.location.state?.backgroundLocation?.state?.prevUrl
  const mileageRate =
    props.baseFlatFields?.mileageRate !== undefined
      ? props.baseFlatFields?.mileageRate
      : props.mileageRate || 0

  const manageExpenseFieldsValidation =
    (): FormFieldsValidation<ExpenseFields> => ({
      amountCents: !isExpenseTypeMileage
        ? [
            numberIsEmpty('Please enter an amount greater than $0.00'),
            numberMustBeGreaterThan(
              0,
              'Please enter an amount greater than $0.00',
            ),
          ]
        : undefined,
      expenseName: [stringIsEmpty('Please enter expense name')],
      expenseDate: [momentIsEmpty('Please choose a date')],
      distance:
        isExpenseTypeMileage && !useMileageRoute
          ? [
              numberIsEmpty('Please enter miles driven'),
              numberMustBeGreaterThan(0, 'Please enter miles driven'),
            ]
          : undefined,
    })

  const {
    fields: expenseFlatFields,
    setFields: setExpenseFlatFields,
    fieldsErrors: expenseFlatFieldErrors,
    setFieldsErrors: setExpenseFlatFieldsErrors,
    onFieldChange: expenseFlatFieldChange,
    setErrorsAndReturnDidPassValidation: expenseFlatFieldsSetErrors,
  } = useFormFields<ExpenseFields>(
    {
      expenseReportId: props.expenseReportId,
      amountCents: 0,
      ...props.baseFlatFields,
    },
    manageExpenseFieldsValidation,
    props.errorDetailMap,
  )

  const onAddAnotherExpense = (): void => {
    setExpenseFlatFields({
      expenseReportId: props.expenseReportId,
      expenseName: '',
      currencyCode: undefined,
      amountCents: 0,
      expenseDate: undefined,
      notes: '',
      type: ExpenseType.General,
      distance: undefined,
      receiptAttachmentIds: undefined,
    })
    setNotesCharacters(0)
    setAmountToBeReimbursed(0)
    setIsExpenseTypeMileage(false)
    setMileageRouteData(undefined)
    setUseMileageRoute(true)

    if (props.handleScroll) {
      props.handleScroll()
    }
  }

  const onPassValidation = (): void => {
    let url
    if (props.isSubmitter) {
      url = myWalletReimbursementURL(props.expenseReportId)
    } else if (props.isManager) {
      url = managerReviewReimbursementURL(props.expenseReportId)
    } else {
      url = expenseReportUrl(props.expenseReportId)
    }

    dispatch(
      connectedPush({
        pathname: url,
        search: '',
        state: {
          prevUrl: prevUrl || expensesOutOfPocketReportsUrl(),
        },
        hash: '',
      }),
    )
    dispatch(closeModal())
  }

  const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()

    if (
      isExpenseTypeMileage &&
      mileageRouteMapEnabled &&
      useMileageRoute &&
      (!mileageRouteData?.origin || !mileageRouteData?.destination)
    ) {
      setMileageRouteError(
        translateSingleString(
          locale,
          'Please add an origin and destination to calculate your route',
        ),
      )
      return
    }
    setMileageRouteError(undefined)

    const fieldsToSubmit: ExpenseFields = {
      ...expenseFlatFields,
      ...(isExpenseTypeMileage &&
        mileageRouteMapEnabled && {
          useMileageRoute,
          ...(useMileageRoute
            ? {
                distance: undefined,
                mileageRoute: mileageRouteData,
              }
            : {
                distance: expenseFlatFields.distance,
                mileageRoute: null,
              }),
        }),
    }

    if (props.expense === undefined || props.fieldsEditable) {
      if (expenseFlatFieldsSetErrors()) {
        props.onSuccessfulSubmit(
          fieldsToSubmit,
          amountToBeReimbursed,
          isExpenseTypeMileage,
        )
        onPassValidation()
      }
    } else {
      props.onSuccessfulSubmit(
        fieldsToSubmit,
        amountToBeReimbursed,
        isExpenseTypeMileage,
      )
      onPassValidation()
    }
  }

  const getDecimalPlaces = (num: number): number => {
    const index = num.toString().indexOf('.')
    if (index === -1) {
      return 0
    }
    return num.toString().length - index - 1
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        row-gap: 32px;
        padding: 40px 60px 60px;
        background-color: ${styleConstants.colors.gray_light};
        position: relative;
        ${getBreakpointDown(breakpointSizes.XSM)(css`
          padding: 40px 20px 120px;
        `)}
      `}
    >
      {props.fieldsEditable ? (
        <form
          onSubmit={handleOnSubmit}
          css={css`
            display: flex;
            flex-direction: column;
            row-gap: 32px;
          `}
        >
          <Box
            padding="40px"
            css={css`
              display: flex;
              flex-direction: column;
              box-shadow: 0px 3px 10px 0px rgba(0, 0, 0, 0.1);
            `}
          >
            {props.baseFlatFields !== undefined ? (
              <>
                <span
                  css={css`
                    ${getFontCss(
                      14,
                      styleConstants.colors.gray_darkest,
                      styleConstants.fontFamilies.regular,
                    )};
                    margin-bottom: 8px;
                  `}
                >
                  {translate('Expense Type')}
                </span>
                <span
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.regular,
                    )};
                  `}
                >
                  {props.expense?.type === ExpenseType.Mileage
                    ? translate('Mileage')
                    : translate('General')}
                </span>
              </>
            ) : (
              <TextDropdown
                fieldLabel={translate('Expense Type')}
                chooseOption={(val: string): void => {
                  expenseFlatFieldChange('type', val)
                  setIsExpenseTypeMileage(val === ExpenseType.Mileage)
                  setExpenseFlatFieldsErrors({
                    expenseName: undefined,
                    amountCents: undefined,
                    expenseDate: undefined,
                    distance: undefined,
                  })
                }}
                isMulti={false}
                baseValues={new Set([ExpenseType.General])}
                selectedValues={
                  expenseFlatFields['type'] !== undefined
                    ? new Set([expenseFlatFields['type']])
                    : new Set([ExpenseType.General])
                }
                errors={expenseFlatFieldErrors['type']}
                optionGroups={[
                  {
                    options: [
                      {
                        value: ExpenseType.General,
                        details: {
                          text: translateSingleString(locale, 'General'),
                        },
                      },
                      {
                        value: ExpenseType.Mileage,
                        details: {
                          text: translateSingleString(locale, 'Mileage'),
                        },
                      },
                    ],
                  },
                ]}
              />
            )}
          </Box>
          <Box
            padding="40px"
            css={css`
              display: flex;
              flex-direction: column;
              gap: 32px;
              box-shadow: 0px 3px 10px 0px rgba(0, 0, 0, 0.1);
            `}
          >
            <TextInputField
              errors={expenseFlatFieldErrors['expenseName']}
              design={TextInputFieldDesigns.New}
              fieldLabel={translate('Expense Name')}
              baseValue={props.baseFlatFields?.expenseName}
              value={expenseFlatFields['expenseName']}
              onChange={(val: string): void => {
                expenseFlatFieldChange('expenseName', val)
              }}
              placeholder={translateSingleString(
                locale,
                isExpenseTypeMileage
                  ? 'e.g. Driving to client meeting'
                  : 'Try to be concise',
              )}
              css={css`
                width: 100%;
              `}
            />
            <SingleDateInputField
              fieldLabel={translate(
                isExpenseTypeMileage ? 'Date Traveled' : 'Date of Purchase',
              )}
              errors={expenseFlatFieldErrors['expenseDate']}
              baseValue={props.baseFlatFields?.expenseDate}
              dateMoment={
                expenseFlatFields['expenseDate'] === undefined
                  ? undefined
                  : moment(expenseFlatFields['expenseDate'])
              }
              onDateMomentChange={(val): void =>
                expenseFlatFieldChange('expenseDate', moment(val))
              }
              allowPastAndCurrentDates
            />
            <div>
              {isExpenseTypeMileage ? (
                mileageRouteMapEnabled ? (
                  <MileageRouteSection
                    existingRoute={
                      props.baseFlatFields?.mileageRoute ?? undefined
                    }
                    useMileageRoute={useMileageRoute}
                    onDistanceComputed={(miles: number): void => {
                      setAmountToBeReimbursed(miles * mileageRate)
                    }}
                    onRouteDataChanged={(data): void => {
                      setMileageRouteData(data)
                      setMileageRouteError(undefined)
                    }}
                    onUseMileageRouteChanged={(val): void => {
                      setUseMileageRoute(val)
                      setMileageRouteError(undefined)
                      if (val) {
                        setAmountToBeReimbursed(
                          (mileageRouteData?.distance ?? 0) * mileageRate,
                        )
                      } else {
                        setAmountToBeReimbursed(
                          (expenseFlatFields.distance ?? 0) * mileageRate,
                        )
                      }
                    }}
                  >
                    {useMileageRoute ? (
                      <div
                        css={css`
                          display: flex;
                          flex-direction: column;
                          gap: 32px;
                        `}
                      >
                        <div>
                          <span
                            css={css`
                              ${getFontCss(
                                14,
                                styleConstants.colors.gray_darkest,
                                styleConstants.fontFamilies.regular,
                              )};
                              display: block;
                            `}
                          >
                            {translate('Distance')}
                          </span>
                          <span
                            css={css`
                              ${getFontCss(
                                14,
                                themeConstants.colors.font_primary,
                                styleConstants.fontFamilies.regular,
                              )};
                              display: block;
                              margin-top: 8px;
                              line-height: 20px;
                            `}
                          >
                            {mileageRouteData?.distance ?? 0}{' '}
                            {translateSingleString(locale, 'Miles')}
                          </span>
                        </div>
                        <div>
                          <span
                            css={css`
                              ${getFontCss(
                                14,
                                styleConstants.colors.gray_darkest,
                                styleConstants.fontFamilies.regular,
                              )};
                              display: block;
                            `}
                          >
                            {translate('Amount to be reimbursed')}
                          </span>
                          <span
                            css={css`
                              display: flex;
                              align-items: baseline;
                              gap: 8px;
                              margin-top: 8px;
                            `}
                          >
                            <span
                              css={css`
                                ${getFontCss(
                                  14,
                                  themeConstants.colors.font_primary,
                                  styleConstants.fontFamilies.regular,
                                )};
                              `}
                            >
                              <Crncy
                                currency={ExtendCurrency.Usd}
                                amount={amountToBeReimbursed}
                              />
                            </span>
                            <span
                              css={css`
                                ${getFontCss(
                                  12,
                                  styleConstants.colors.gray_darkest,
                                  styleConstants.fontFamilies.regular,
                                )};
                              `}
                            >
                              {translate('@ ${mileageRate}/mile', {
                                mileageRate: (
                                  <Crncy
                                    currency={ExtendCurrency.Usd}
                                    amount={mileageRate}
                                    maximumFractionDigits={
                                      getDecimalPlaces(mileageRate) > 2 ? 3 : 2
                                    }
                                  />
                                ),
                              })}
                            </span>
                          </span>
                        </div>
                      </div>
                    ) : (
                      <>
                        <AmountInputField
                          errors={expenseFlatFieldErrors['distance']}
                          fieldLabel={translate('Distance')}
                          baseValue={props.baseFlatFields?.distance}
                          value={expenseFlatFields['distance']}
                          onChange={(val: number | undefined): void => {
                            expenseFlatFieldChange('distance', val)
                            setAmountToBeReimbursed(
                              val !== undefined ? val * mileageRate : 0,
                            )
                          }}
                          placeholder={translateSingleString(
                            locale,
                            'Enter Distance',
                          )}
                          unitOfLength={translateSingleString(locale, 'Miles')}
                          maxValue={99999999}
                          isRequest={false}
                        />
                        <div>
                          <span
                            css={css`
                              ${getFontCss(
                                14,
                                styleConstants.colors.gray_darkest,
                                styleConstants.fontFamilies.regular,
                              )};
                              display: block;
                            `}
                          >
                            {translate('Amount to be reimbursed', {
                              amount: (
                                <Crncy
                                  currency={ExtendCurrency.Usd}
                                  amount={amountToBeReimbursed}
                                />
                              ),
                            })}
                          </span>
                          <span
                            css={css`
                              display: flex;
                              align-items: baseline;
                              gap: 8px;
                              margin-top: 8px;
                            `}
                          >
                            <span
                              css={css`
                                ${getFontCss(
                                  14,
                                  themeConstants.colors.font_primary,
                                  styleConstants.fontFamilies.regular,
                                )};
                              `}
                            >
                              <Crncy
                                currency={ExtendCurrency.Usd}
                                amount={amountToBeReimbursed}
                              />
                            </span>
                            <span
                              css={css`
                                ${getFontCss(
                                  12,
                                  styleConstants.colors.gray_darkest,
                                  styleConstants.fontFamilies.regular,
                                )};
                              `}
                            >
                              {translate('@ ${mileageRate}/mile', {
                                mileageRate: (
                                  <Crncy
                                    currency={ExtendCurrency.Usd}
                                    amount={mileageRate}
                                    maximumFractionDigits={
                                      getDecimalPlaces(mileageRate) > 2 ? 3 : 2
                                    }
                                  />
                                ),
                              })}
                            </span>
                          </span>
                        </div>
                      </>
                    )}
                  </MileageRouteSection>
                ) : (
                  <>
                    <AmountInputField
                      errors={expenseFlatFieldErrors['distance']}
                      fieldLabel={translate('Distance')}
                      baseValue={props.baseFlatFields?.distance}
                      value={expenseFlatFields['distance']}
                      onChange={(val: number | undefined): void => {
                        expenseFlatFieldChange('distance', val)
                        setAmountToBeReimbursed(
                          val !== undefined ? val * mileageRate : 0,
                        )
                      }}
                      placeholder={translateSingleString(
                        locale,
                        'Enter Distance',
                      )}
                      unitOfLength={translateSingleString(locale, 'Miles')}
                      maxValue={99999999}
                      isRequest={false}
                    />
                    <div>
                      <span
                        css={css`
                          ${getFontCss(
                            14,
                            styleConstants.colors.gray_darkest,
                            styleConstants.fontFamilies.regular,
                          )};
                          display: block;
                        `}
                      >
                        {translate('Amount to be reimbursed', {
                          amount: (
                            <Crncy
                              currency={ExtendCurrency.Usd}
                              amount={amountToBeReimbursed}
                            />
                          ),
                        })}
                      </span>
                      <span
                        css={css`
                          display: inline-block;
                          margin-top: 8px;
                        `}
                      >
                        <span
                          css={css`
                            ${getFontCss(
                              14,
                              themeConstants.colors.font_primary,
                              styleConstants.fontFamilies.regular,
                            )};
                          `}
                        >
                          <Crncy
                            currency={ExtendCurrency.Usd}
                            amount={amountToBeReimbursed}
                          />
                        </span>
                        <span
                          css={css`
                            ${getFontCss(
                              12,
                              styleConstants.colors.gray_darkest,
                              styleConstants.fontFamilies.regular,
                            )};
                            margin-left: 10px;
                          `}
                        >
                          {translate('@ ${mileageRate}/mile', {
                            mileageRate: (
                              <Crncy
                                currency={ExtendCurrency.Usd}
                                amount={mileageRate}
                                maximumFractionDigits={
                                  getDecimalPlaces(mileageRate) > 2 ? 3 : 2
                                }
                              />
                            ),
                          })}
                        </span>
                      </span>
                    </div>
                  </>
                )
              ) : (
                <AmountInputField
                  baseValue={props.baseFlatFields?.amountCents || 0}
                  fieldLabel={translate('Amount Spent')}
                  value={expenseFlatFields['amountCents']}
                  onChange={(val: number | undefined): void => {
                    expenseFlatFieldChange('amountCents', val)
                  }}
                  errors={expenseFlatFieldErrors['amountCents']}
                  maxValue={99999999}
                  currency={ExtendCurrency.Usd}
                  isRequest={false}
                />
              )}
            </div>
          </Box>
          {!isExpenseTypeMileage && (
            <ReceiptsBox
              attachments={props.receiptAttachments}
              uploadAttachments={props.uploadAttachments}
              uploadingAttachmentThumbnails={
                props.uploadingAttachmentThumbnails
              }
              deleteAttachment={props.deleteAttachment}
              isReceiptRequired={
                props.baseFlatFields !== undefined &&
                props.expenseReportStatus === ExpenseReportStatus.Draft
              }
            />
          )}
          <Box
            padding="40px"
            css={css`
              display: flex;
              flex-direction: column;
              box-shadow: 0px 3px 10px 0px rgba(0, 0, 0, 0.1);
            `}
          >
            <NotesInput
              notesProps={{
                showUpdates: true,
                baseValue: props.baseFlatFields?.notes,
                value: expenseFlatFields.notes,
                onChange: (val: string): void => {
                  expenseFlatFieldChange('notes', val)
                  setNotesCharacters(val.length)
                },
              }}
              showMissingTag
              collapsible
              maxLength={2000}
              notesCharacters={notesCharacters}
              isExpense
            />
          </Box>
          {props.topLevelError !== undefined && (
            <ErrorList errors={[props.topLevelError]} />
          )}
          {mileageRouteError && (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 6px;
                font-size: 14px;
                color: ${styleConstants.colors.red};
              `}
              role="alert"
            >
              <AlertCircleFilledIcon />
              <span>{mileageRouteError}</span>
            </div>
          )}
          <div
            css={css`
              display: flex;
              flex-direction: row;
              align-items: center;
              ${props.negativeButton !== undefined &&
              css`
                justify-content: space-between;
              `}
            `}
          >
            <Button
              css={css`
                width: 205px;
              `}
              type={ButtonTypes.submit}
              design={ButtonDesigns.designCta}
              big
              text={props.buttonText}
              loading={props.loading}
              testId="expense-form-submit"
            />
            {props.negativeButton !== undefined &&
            props.expenseReportStatus === ExpenseReportStatus.Draft ? (
              <span
                onKeyPress={(): null => {
                  return null
                }}
                role="button"
                tabIndex={0}
              >
                <Button
                  id={'rejectButton'}
                  design={ButtonDesigns.designRed}
                  type={ButtonTypes.button}
                  big
                  text={props.negativeButton.text}
                  onClick={props.negativeButton?.onClick}
                  loading={props.negativeButton.loading}
                />
              </span>
            ) : (
              <HoverableLink
                key={0}
                css={css`
                  display: flex;
                  align-items: center;
                  padding-left: 20px;
                `}
                onClick={(): void => {
                  if (expenseFlatFieldsSetErrors()) {
                    props.onSuccessfulSubmit(
                      expenseFlatFields,
                      amountToBeReimbursed,
                      isExpenseTypeMileage,
                    )
                    onAddAnotherExpense()
                  }
                }}
                to={addExpenseUrl(props.expenseReportId)}
                ariaLabelSource={'Add Another Expense'}
              >
                <SvgExpand
                  css={css`
                    transform: scale(calc(14 / 24));
                  `}
                />
                {translate('Add Another Expense')}
              </HoverableLink>
            )}
          </div>
          {props.negativeButton === undefined && (
            <span
              css={css`
                ${getFontCss(
                  12,
                  styleConstants.colors.gray_darkest,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              {translate('Adding another expense will save your progress')}
            </span>
          )}
        </form>
      ) : (
        <>
          <ExpenseFlyoutContent
            expenseReportId={props.expenseReportId}
            receiptAttachments={props.receiptAttachments}
            expenseFlatFields={props.baseFlatFields}
            notesAndReceiptsEditable={props.notesAndReceiptsEditable}
            isExpenseTypeMileage={isExpenseTypeMileage}
            mileageRate={mileageRate}
            mileageRoute={
              mileageRouteMapEnabled
                ? (props.baseFlatFields?.mileageRoute ?? undefined)
                : undefined
            }
          />
          {props.notesAndReceiptsEditable && (
            <form
              onSubmit={handleOnSubmit}
              css={css`
                display: flex;
                flex-direction: column;
                row-gap: 32px;
              `}
            >
              {!isExpenseTypeMileage && (
                <ReceiptsBox
                  attachments={props.receiptAttachments}
                  uploadAttachments={props.uploadAttachments}
                  uploadingAttachmentThumbnails={
                    props.uploadingAttachmentThumbnails
                  }
                  deleteAttachment={props.deleteAttachment}
                  isReceiptRequired={false}
                  errors={props.receiptAttachments.length === 1}
                />
              )}
              <Box
                padding="40px"
                css={css`
                  display: flex;
                  flex-direction: column;
                `}
              >
                <NotesInput
                  notesProps={{
                    showUpdates: true,
                    baseValue: props.baseFlatFields?.notes,
                    value: expenseFlatFields.notes,
                    onChange: (val: string): void => {
                      expenseFlatFieldChange('notes', val)
                      setNotesCharacters(val.length)
                    },
                  }}
                  showMissingTag
                  collapsible
                  maxLength={2000}
                  notesCharacters={notesCharacters}
                  isExpense
                />
              </Box>
              <div
                css={css`
                  display: flex;
                  flex-direction: row;
                  align-items: center;
                  ${props.negativeButton !== undefined &&
                  css`
                    justify-content: space-between;
                  `}
                `}
              >
                <Button
                  css={css`
                    width: 200px;
                  `}
                  type={ButtonTypes.submit}
                  design={ButtonDesigns.designCta}
                  big
                  text={props.buttonText}
                  loading={props.loading}
                  testId="expense-form-submit"
                />
              </div>
            </form>
          )}
        </>
      )}
    </div>
  )
}

export default ExpenseForm
